// client/src/components/ticketing/OrderTicketsPage.jsx
//
// Public, login-less "My Tickets" page reached via /tickets/:accessToken —
// the magic link in the ticket confirmation / reminder emails. Same
// standalone-public-page pattern as EventLandingPage.jsx (no AppHeader /
// Sidebar / footer, explicit page states).

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { calendarLinks } from '../../utils/calendarLinks';

function formatDateRange(startdate, enddate) {
  if (!startdate) return '';
  try {
    const start = new Date(startdate);
    const end = enddate ? new Date(enddate) : null;
    const opts = { dateStyle: 'medium', timeStyle: 'short' };
    const s = start.toLocaleString(undefined, opts);
    return end ? `${s} – ${end.toLocaleString(undefined, opts)}` : s;
  } catch { return String(startdate); }
}

export default function OrderTicketsPage() {
  const { accessToken } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | not_found | error
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: resp } = await axios.get(`/api/ticketing/public/order/${accessToken}`);
        if (cancelled) return;
        setData(resp);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setState(err?.response?.status === 404 ? 'not_found' : 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [accessToken]);

  if (state === 'loading') return <Shell><p className="opacity-70">Loading your tickets…</p></Shell>;
  if (state === 'not_found') return <Shell><p>We couldn't find that order. The link may be out of date.</p></Shell>;
  if (state === 'error') return <Shell><p>Something went wrong. Please try again shortly.</p></Shell>;

  const { order, event, tickets, calendarUrl } = data;
  const cal = calendarLinks({
    name: event.name,
    description: '',
    start: event.startdate,
    end: event.enddate,
    icsUrl: calendarUrl,
  });

  return (
    <Shell>
      <h1 className="text-2xl font-semibold mb-1">{event.name}</h1>
      <p className="opacity-70 mb-4">{formatDateRange(event.startdate, event.enddate)}</p>

      <div className="rounded-xl border border-black/10 dark:border-white/10 p-4 mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="opacity-60">Order</span>
          <span>#{order.id}</span>
        </div>
        <div className="flex justify-between text-sm mb-2">
          <span className="opacity-60">Ticket type</span>
          <span>{event.tierName}</span>
        </div>
        <div className="flex justify-between text-sm mb-2">
          <span className="opacity-60">Status</span>
          <span className={order.status === 'refunded' ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}>
            {order.status}
          </span>
        </div>
        {order.refunded > 0 && (
          <div className="flex justify-between text-sm">
            <span className="opacity-60">Refunded</span>
            <span>{order.currency?.toUpperCase()} {order.refunded.toFixed(2)}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4 text-sm">
        {cal.ics && <a className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15" href={cal.ics}>Add to calendar (.ics)</a>}
        {cal.google && <a className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15" href={cal.google} target="_blank" rel="noreferrer">Google</a>}
        {cal.outlook && <a className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15" href={cal.outlook} target="_blank" rel="noreferrer">Outlook</a>}
      </div>

      <div className="space-y-2">
        {tickets.map((t, i) => (
          <div key={t.id} className="flex items-center justify-between rounded-lg border border-black/10 dark:border-white/10 p-3 text-sm">
            <div>
              <div className="font-medium">{t.attendeeName || `Ticket ${i + 1}`}</div>
              <div className="text-xs opacity-60">{t.status}{t.checkedInAt ? ' · checked in' : ''}</div>
            </div>
            {t.status !== 'refunded' && t.status !== 'cancelled' && (
              <a href={t.badgePdfUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded bg-cobalt text-white text-sm font-medium">
                Download PDF
              </a>
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-black dark:text-white">
      <div className="max-w-lg mx-auto p-6">{children}</div>
    </div>
  );
}
